import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  setDoc,
  onSnapshot, 
  query, 
  orderBy, 
  writeBatch,
  increment,
  limit,
  where,
  getDocs,
  getDoc
} from 'firebase/firestore';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [projectMessages, setProjectMessages] = useState([]);
  const [sentEmails, setSentEmails] = useState([]); 
  const [attendance, setAttendance] = useState([]);
  const [activeAttendance, setActiveAttendance] = useState(null);
  const [activeTimer, setActiveTimer] = useState(null);
  const [loading, setLoading] = useState(true);

  const defaultSettings = {
    smtp: { host: 'smtp.gmail.com', port: '587', username: '', password: '', fromName: 'CRM System', fromEmail: '', encryption: 'tls', enabled: true },
    branding: { appName: 'CRM Pro', logoUrl: '', primaryColor: '#2563eb', companyName: 'My Company', footerText: '© 2024 All rights reserved.' }
  };

  const [settings, setSettings] = useState(defaultSettings);

  useEffect(() => {
    if (!user) {
      setTasks([]); setProjects([]); setClients([]); setTeamMembers([]); setLeaves([]); setProjectMessages([]); setSentEmails([]); setAttendance([]); setActiveAttendance(null); setActiveTimer(null);
      return;
    }

    setLoading(true);

    // Core Data Subscriptions
    const unsubTasks = onSnapshot(query(collection(db, 'tasks'), orderBy('createdAt', 'desc')), (s) => setTasks(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubProjects = onSnapshot(query(collection(db, 'projects'), orderBy('createdAt', 'desc')), (s) => setProjects(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubClients = onSnapshot(query(collection(db, 'clients'), orderBy('createdAt', 'desc')), (s) => setClients(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubUsers = onSnapshot(query(collection(db, 'users')), (s) => setTeamMembers(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubLeaves = onSnapshot(query(collection(db, 'leaves'), orderBy('createdAt', 'desc')), (s) => setLeaves(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubMessages = onSnapshot(query(collection(db, 'messages'), orderBy('createdAt', 'asc')), (s) => setProjectMessages(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubEmails = onSnapshot(query(collection(db, 'sent_emails'), orderBy('sentAt', 'desc'), limit(50)), (s) => setSentEmails(s.docs.map(d => ({ id: d.id, ...d.data() }))));

    const unsubSettings = onSnapshot(doc(db, 'settings', 'global'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSettings({ branding: { ...defaultSettings.branding, ...data.branding }, smtp: { ...defaultSettings.smtp, ...(data.smtp || data.email) } });
      }
    });

    const isAdmin = user.role === 'super_admin' || user.role === 'manager';
    const attendanceQuery = isAdmin 
      ? query(collection(db, 'attendance'), orderBy('startTime', 'desc'), limit(1000))
      : query(collection(db, 'attendance'), where('userId', '==', user.id), orderBy('startTime', 'desc'));

    const unsubAttendance = onSnapshot(attendanceQuery, (snapshot) => {
      const logs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setAttendance(logs);
      const active = logs.find(log => log.userId === user.id && log.status === 'active');
      setActiveAttendance(active || null);
    });

    const unsubActiveTimer = onSnapshot(doc(db, 'active_timers', user.id), (docSnap) => {
      if (docSnap.exists()) {
        setActiveTimer(docSnap.data());
      } else {
        setActiveTimer(null);
      }
    });

    setLoading(false);
    return () => {
      unsubTasks(); unsubProjects(); unsubClients(); unsubUsers(); unsubLeaves(); unsubMessages(); unsubSettings(); unsubEmails(); unsubAttendance(); unsubActiveTimer();
    };
  }, [user]);

  // --- Task Timer Actions ---
  const addTimeLog = async (taskId, entry) => {
    const taskRef = doc(db, 'tasks', taskId);
    const taskSnap = await getDoc(taskRef);
    if (taskSnap.exists()) {
      const taskData = taskSnap.data();
      await updateDoc(taskRef, { timeLog: [...(taskData.timeLog || []), { id: Date.now().toString(), userId: user.id, ...entry, date: new Date().toISOString() }] });
    }
  };

  const startTimer = async (taskId, description) => {
    try {
      await setDoc(doc(db, 'active_timers', user.id), { taskId, startTime: Date.now(), description, userId: user.id });
    } catch (e) { console.error(e); }
  };

  const stopTimer = async () => {
    if (!activeTimer) return;
    try {
      const duration = (Date.now() - activeTimer.startTime) / 3600000;
      await addTimeLog(activeTimer.taskId, { 
        hours: Math.max(0.01, Math.round(duration * 100) / 100), 
        description: activeTimer.description 
      });
      await deleteDoc(doc(db, 'active_timers', user.id));
    } catch (e) {
      console.error("Error stopping timer:", e);
    }
  };

  // --- Attendance Actions ---
  const clockIn = async (note = '') => {
    if (activeAttendance) return { success: false, error: 'Already clocked in' };
    try {
      await addDoc(collection(db, 'attendance'), {
        userId: user.id,
        userName: user.name,
        startTime: new Date().toISOString(),
        status: 'active',
        note,
        device: navigator.userAgent.split(') ')[0].split(' (')[1] || 'Web Browser'
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const clockOut = async () => {
    try {
      // 1. AUTO-STOP TIMER: If there's an active task timer, stop it first
      if (activeTimer) {
        await stopTimer();
      }

      // 2. QUERY ACTIVE ATTENDANCE: Find all active shift sessions for this user
      const q = query(collection(db, 'attendance'), where('userId', '==', user.id), where('status', '==', 'active'));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) return { success: true };

      // 3. BATCH UPDATE: Close all active shift sessions
      const batch = writeBatch(db);
      const endTime = new Date().toISOString();
      querySnapshot.forEach((document) => {
        batch.update(doc(db, 'attendance', document.id), {
          endTime: endTime,
          status: 'completed'
        });
      });
      
      await batch.commit();
      return { success: true };
    } catch (error) {
      console.error("Clock out error:", error);
      return { success: false, error: error.message };
    }
  };

  // --- CRUD Helpers ---
  const createTask = async (data) => await addDoc(collection(db, 'tasks'), { ...data, createdAt: new Date().toISOString(), comments: [], timeLog: [] });
  const updateTask = async (id, data) => await updateDoc(doc(db, 'tasks', id), data);
  const deleteTask = async (id) => await deleteDoc(doc(db, 'tasks', id));
  
  const addComment = async (taskId, comment) => {
    const taskRef = doc(db, 'tasks', taskId);
    const taskSnap = await getDoc(taskRef);
    if (taskSnap.exists()) {
      const taskData = taskSnap.data();
      await updateDoc(taskRef, { comments: [...(taskData.comments || []), { id: Date.now().toString(), userId: user.id, text: comment, createdAt: new Date().toISOString() }] });
    }
  };

  const createProject = async (data) => await addDoc(collection(db, 'projects'), { ...data, createdAt: new Date().toISOString(), progress: 0, milestones: [], risks: [], files: [], budget: data.budget || { total: 0, spent: 0, currency: 'USD' } });
  const updateProject = async (id, data) => await updateDoc(doc(db, 'projects', id), data);
  const addProjectMessage = async (projectId, text) => await addDoc(collection(db, 'messages'), { projectId, userId: user.id, text, createdAt: new Date().toISOString() });
  const createClient = async (data) => await addDoc(collection(db, 'clients'), { ...data, createdAt: new Date().toISOString(), lastContact: new Date().toISOString() });
  const updateClient = async (id, data) => await updateDoc(doc(db, 'clients', id), data);
  const deleteClient = async (id) => await deleteDoc(doc(db, 'clients', id));
  const updateTeamMember = async (id, data) => await updateDoc(doc(db, 'users', id), data);
  const deleteTeamMember = async (id) => await deleteDoc(doc(db, 'users', id));
  const requestLeave = async (data) => await addDoc(collection(db, 'leaves'), { userId: user.id, ...data, status: 'pending', createdAt: new Date().toISOString() });
  const updateLeaveStatus = async (id, status) => await updateDoc(doc(db, 'leaves', id), { status });
  const updateLeave = async (id, data) => await updateDoc(doc(db, 'leaves', id), data);
  const deleteLeave = async (id) => await deleteDoc(doc(db, 'leaves', id));
  const bulkUpdateLeaveBalances = async (ids, casual, paid) => {
    const batch = writeBatch(db);
    ids.forEach(id => batch.update(doc(db, 'users', id), { "leaveBalance.casual": increment(Number(casual)), "leaveBalance.paid": increment(Number(paid)) }));
    await batch.commit();
  };
  const updateSettings = async (section, data) => await setDoc(doc(db, 'settings', 'global'), { [section]: data }, { merge: true });
  
  const sendEmail = async (to, subject, message, overrideConfig = null) => {
    const config = overrideConfig || settings.smtp;
    try {
      const response = await fetch('/.netlify/functions/send-email', { method: 'POST', body: JSON.stringify({ to, subject, message, config }), headers: { 'Content-Type': 'application/json' } });
      if (response.ok) {
        await addDoc(collection(db, 'sent_emails'), { to, from: `${config.fromName} <${config.fromEmail || config.username}>`, subject, body: message, status: 'sent', sentAt: new Date().toISOString() });
        return { success: true };
      }
      throw new Error("Failed to send");
    } catch (e) {
      await addDoc(collection(db, 'sent_emails'), { to, from: `${config.fromName} <${config.fromEmail || config.username}>`, subject, body: message, status: 'failed', error: e.message, sentAt: new Date().toISOString() });
      return { success: false, error: e.message };
    }
  };

  return (
    <DataContext.Provider value={{
      tasks, projects, clients, teamMembers, leaves, projectMessages, sentEmails, activeTimer, attendance, activeAttendance, loading, settings,
      createTask, updateTask, deleteTask, addComment, addTimeLog,
      createProject, updateProject, addProjectMessage,
      createClient, updateClient, deleteClient,
      updateTeamMember, deleteTeamMember,
      requestLeave, updateLeaveStatus, updateLeave, deleteLeave, bulkUpdateLeaveBalances,
      startTimer, stopTimer,
      updateSettings, sendEmail,
      clockIn, clockOut
    }}>
      {children}
    </DataContext.Provider>
  );
};